import { useUserDeviceEventsStream } from '@/api';
import { DownArrowIcon, RightArrowIcon, UpArrowIcon } from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import { ContactSensorIcon, DoorClosedIcon, DoorOpenIcon } from '@/Components/Dashboard/icons';
import {
  DEVICE_EVENT_TYPES,
  DEVICE_EVENTS_PROPERTY,
  DEVICE_STATE_PROPERTY,
  DEVICE_TYPES,
  EVENT_PROPERTIES,
  EVENT_TYPES,
} from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  formatTime,
  getAccessoryUuids,
  getDateLabel,
  HistoryModalTable,
  LoadingCard,
  RECENT_ACTIVITY_HISTORY_DAYS,
  sortByLatestTimestamp,
} from '../RecentActivityUtils';

const deviceTypes = [DEVICE_TYPES.DOOR_WINDOW_SENSOR];
const eventTypes = deviceTypes.flatMap((type) => DEVICE_EVENT_TYPES[type] || []);

const getDoorWindowState = (event = {}) => {
  const statePaths = [
    DEVICE_EVENTS_PROPERTY.accessory_state_update_door_window_sensor_open,
    DEVICE_STATE_PROPERTY.door_window_sensor,
    DEVICE_STATE_PROPERTY.door_window_sensor_ble,
    DEVICE_EVENTS_PROPERTY.accessory_state_ble_door_window_sensor,
  ];

  for (const path of statePaths) {
    const value = get(event, path, undefined);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return '';
};

const renderCells = (entry, { trans }) => {
  const state = getDoorWindowState(entry);
  const Icon = state !== '' ? (state ? DoorOpenIcon : DoorClosedIcon) : null;
  return (
    <>
      <div className="row-cell bp-cell">{get(entry, EVENT_PROPERTIES.LOCATION, '-')}</div>
      <div className="row-cell bp-cell">
        {Icon && <Icon className="state-icon" />}
        {state !== '' ? (state ? trans('OPEN') : trans('CLOSED')) : '-'}
      </div>
    </>
  );
};

const ModalContent = ({ useruuid, deviceUuids, ...displayProps }) => {
  const trans = useSelector(translator);
  const history = useUserDeviceEventsStream({
    useruuid,
    deviceuuid: deviceUuids,
    eventTypes,
    days: RECENT_ACTIVITY_HISTORY_DAYS,
  });

  return (
    <HistoryModalTable
      {...displayProps}
      {...history}
      headers={[trans('DATE_L'), trans('LOCATION'), trans('STATE')]}
      renderCells={renderCells}
    />
  );
};

ModalContent.propTypes = {
  useruuid: PropTypes.string,
  deviceUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export const SmartHomeDoorWindowCard = (props) => {
  const { useruuid, timezone, locale, is24HourFormat, accessories, data, isLoading, trendData } =
    props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();

  // One request covers every sensor; the API accepts a list of device UUIDs.
  const deviceUuids = useMemo(() => getAccessoryUuids(accessories, deviceTypes), [accessories]);

  const latestEvent = useMemo(() => {
    if (isEmpty(data)) return {};
    const items = Array.isArray(data) ? data : [];
    const filteredData = items.filter((event) =>
      [
        EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_UPDATE_DOOR_WINDOW_SENSOR_OPEN,
        EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_BLE_DOOR_WINDOW_SENSOR,
      ].includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
    );
    const sortedData = sortByLatestTimestamp(filteredData, EVENT_PROPERTIES.TIMESTAMP);
    return sortedData[0];
  }, [data]);

  const openHistoryModal = () =>
    openDashboardModal({
      title: (
        <div className="recent-activity-modal-title">
          <ContactSensorIcon className="recent-activity-modal-title-icon" />
          <span>{trans('CONTACT_SENSOR')}</span>
        </div>
      ),
      content: (
        <ModalContent
          useruuid={useruuid}
          deviceUuids={deviceUuids}
          timezone={timezone}
          is24HourFormat={is24HourFormat}
          locale={locale}
        />
      ),
    });

  const handleCardClick = (e) => {
    e.preventDefault();
    openHistoryModal();
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openHistoryModal();
    }
  };

  if (isLoading) return <LoadingCard />;

  const location = get(latestEvent, EVENT_PROPERTIES.LOCATION, '');
  const timestamp = get(latestEvent, EVENT_PROPERTIES.TIMESTAMP, '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const value = getDoorWindowState(latestEvent);
  const valueLabel = value !== '' ? (value ? trans('OPEN') : trans('CLOSED')) : '';

  const trendPercent = get(trendData, 'trendPercent', null);
  const yesterdayCount = get(trendData, 'yesterdayCount', null);
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <ContactSensorIcon className="recent-activity-card-icon" />
      <h4 className="recent-activity-card-title">{trans('CONTACT_SENSOR')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        {location && <p className="recent-activity-context">{location}</p>}
        <p className="recent-activity-data-value">{valueLabel}</p>
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">
            {yesterdayCount !== null
              ? `${trans('YESTERDAY_OPENED')} ${yesterdayCount} ${trans('TIMES')}`
              : trans('YESTERDAY_OPENED_EMPTY')}
          </p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

SmartHomeDoorWindowCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
};
